import { Component, OnDestroy, OnInit, computed, signal, viewChild, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BadgeData } from '@dxo/ng-swift-ui/lib/components/badge/badge.component';
import { BehaviorSubject, Subject, Subscription, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';

import {
  ApiResponseOfSearchClaimResponse,
  ClaimStatusEnum,
  PolicyClient,
  SearchClaimPaginationCriteria,
  SearchClaimRequest,
  SearchClaimSearchCriteria,
  SearchClaimSortCriteria,
  SearchSortDirection
} from '@benefitsui/app/api/benefits-clients';

import { UserContextService } from '@benefitsui/app/services/user-context/user-context.service';
import { UserRole } from '@benefitsui/app/shared/enums/users';
import { environment } from '@benefitsui/environments/environment';
import { FILTER_TYPE, FilterProps, MultiSelectOption, TabsComponent } from '@dxo/ng-swift-ui';

import {
  IClaimSearchParameters,
  IClaimTableItem,
  IDetailsPageLabels,
  IFilters,
  ITableHeader,
  searchClaimDataToClaimTableItem
} from './data/claim-data.types';

import {
  ClientClaimFieldEnum,
  ClaimTabNameEnum,
  ClientClaimTypeEnum,
  clientClaimTypeToClaimType,
  clientClaimFieldToSearchClaimSortColumn,
  searchSortDirectionToSearchClaimSortDirection
} from './data/claim-data-enums';

import { LocalClaimsDataReaderService } from './data/local-claims-data-reader.service';
import { planCodesToClaimTypeMapping, LIFE_PLAN_CODES, NON_LIFE_PLAN_CODES } from './data/plan-code-mapping';
import { ClaimsServiceClient } from './data/claims-client.service';
import { PolicyService } from '@benefitsui/app/services/policy/policy.service';

// import { mockApiClaimsData } from './data/mock-api-claims-data';

@Component({
  selector: 'app-claims-list',
  templateUrl: './claims-list.component.html',
  styleUrls: ['./claims-list.component.scss'],
  standalone: false
})
export class ClaimsListComponent implements OnInit, OnDestroy {
  /*=== Private properties ===*/
  private claimsServiceClient = inject(ClaimsServiceClient);
  private claimsSignal = signal<IClaimTableItem[]>([]);
  private claimTypeFiltersSignal = signal<MultiSelectOption[]>([]);
  private localClaimsDataReaderService = inject(LocalClaimsDataReaderService);
  private page$ = new BehaviorSubject<number>(1);
  private pageSize$ = new BehaviorSubject<number>(10);
  private policyClient = inject(PolicyClient);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private searchInputSubject = new Subject<string>();
  private searchSubscription: Subscription | null = null;
  private searchText$ = new BehaviorSubject<string>('');
  private searchTextSignal = signal<string>('');
  private sortDirection$ = new BehaviorSubject<SearchSortDirection>(SearchSortDirection.Ascending);
  private sortField$ = new BehaviorSubject<ClientClaimFieldEnum>(ClientClaimFieldEnum.Name);
  private userContextService = inject(UserContextService);
  private policyService = inject(PolicyService);

  /*=== Public properties ===*/
  cardBody = '';
  claimsBodyHeader = 'Search claims';
  ClaimField = ClientClaimFieldEnum;
  ClaimSortDirection = SearchSortDirection;
  ClaimTabNameEnum = ClaimTabNameEnum;
  claimsTableItems: IClaimTableItem[] = [];
  claimsTableFilters: IFilters = {
    claimTypes: []
  };

  combineTabsSignal = signal(false);
  
  get combineTabs(): boolean {
    return this.combineTabsSignal();
  }
  
  set combineTabs(value: boolean) {
    this.combineTabsSignal.set(value);
  }
  detailsButtonLabel = '';
  detailsURL = '';
  filters: FilterProps[] = [];
  isLoading = true;
  planCodes: string[] = [];
  policyNumbers: string[] = [];
  searchInputLabel = 'Type a name, claim#, status, or division';

  searchParameter: IClaimSearchParameters = {
    filters: this.claimsTableFilters,
    page: 1,
    pageSize: 10,
    searchText: '',
    sortField: ClientClaimFieldEnum.Received,
    sortDirection: SearchSortDirection.Descending
  };

  searchText = '';
  selectedTab = signal<ClaimTabNameEnum>(ClaimTabNameEnum.Active);
  showRedesignMessage = false;

  tabBadgeCounts = {
    [ClaimTabNameEnum.Active]: 0,
    [ClaimTabNameEnum.Closed]: 0,
    [ClaimTabNameEnum.All]: 0
  };

  tableHeaders: ITableHeader[] = [];
  readonly tabsComponent = viewChild.required<TabsComponent>('tabsRef');
  private totalClaimsSignal = signal<number>(0);
  
  // Getter for backward compatibility, 
  // too much usage of totalClaims to convert it to signal now
  // TODO: revisit later to see about signal architecture
  get totalClaims(): number {
    return this.totalClaimsSignal();
  }

  /*=== Computed properties ===*/

  // Performance optimization: Convert getters to computed signals for better performance
  readonly emptyClaimsMessage = computed(() => {
    const count = this.filteredClaims().length;
    // Escape searchText to prevent XSS (Cross-Site Scripting)
    const searchText = this.searchTextSignal();
    const safeSearchText = searchText ? this.sanitizeText(searchText) : '';
    
    // When tabs are combined, match the format of resultsCountMessage
    if (this.combineTabsSignal()) {
      if (safeSearchText) {
        const plural = count === 1 ? '' : 's';
        const msg = `There are <strong>${count} result${plural}</strong> for <strong>'${safeSearchText}'</strong>.`;
        return this.sanitizer.bypassSecurityTrustHtml(msg);
      } else {
        const plural = count === 1 ? '' : 's';
        const msg = `There are <strong>${count} total claim${plural}</strong>.`;
        return this.sanitizer.bypassSecurityTrustHtml(msg);
      }
    }
    
    // When tabs are separate, show tab-specific messaging
    const tab = this.selectedTab() || ClaimTabNameEnum.Active;
    // For zero results, always use "are" to match requirement "There are 0..."
    const verb = count === 0 ? 'are' : this.getAreIs(count);
    const plural = count === 1 ? '' : 's';
    let msg = `There ${verb} <strong>${count} ${tab}</strong> claim${plural}`;
    if (safeSearchText) {
      msg += ` for <strong>'${safeSearchText}'</strong>`;
    }
    msg += '.';
    return this.sanitizer.bypassSecurityTrustHtml(msg);
  });

  // Performance optimization: Pre-computed ARIA labels for all sortable headers
  readonly headerAriaLabels = computed(() => {
    const currentField = this.searchParameter.sortField;
    const currentDirection = this.searchParameter.sortDirection;
    const headers = this.headerTexts();

    const labels: Record<string, string> = {};
    Object.keys(headers).forEach((key) => {
      const headerText = headers[key];
      const sortState =
        currentField === key
          ? currentDirection
          : 'not sorted';
      labels[key] = `Sort by ${headerText}, currently sorted ${sortState}`;
    });
    return labels;
  });

  // Performance optimization: Pre-computed ARIA sort values
  readonly headerAriaSortValues = computed(() => {
    const currentField = this.searchParameter.sortField;
    const currentDirection = this.searchParameter.sortDirection;

    const sortValues: Record<string, string> = {};
    [
      ClientClaimFieldEnum.ClaimId,
      ClientClaimFieldEnum.Name,
      ClientClaimFieldEnum.ClaimType,
      ClientClaimFieldEnum.Status,
      ClientClaimFieldEnum.Received,
      ClientClaimFieldEnum.Started,
      ClientClaimFieldEnum.Division,
      ClientClaimFieldEnum.LeaveType
    ].forEach((key) => {
      sortValues[key] =
        currentField === key
          ? currentDirection
          : 'none';
    });
    return sortValues;
  });

  // Performance optimization: Pre-computed header texts to avoid function calls in template
  readonly headerTexts = computed(() => {
    const headers: Record<string, string> = {};
    this.tableHeaders.forEach((header) => {
      headers[header.key] = header.text;
    });
    return headers;
  });

  readonly noClaimsResultsMessage = computed(() => {
    const searchText = this.searchTextSignal();
    if (!searchText || this.combineTabsSignal())
      return { message: this.sanitizer.bypassSecurityTrustHtml(''), tabToShow: null };
    
    const safeSearchText = this.sanitizeText(searchText);
    
    // Check if current tab has results
    const currentTabHasResults = this.filteredClaims().length > 0;
    
    if (currentTabHasResults) {
      return { message: this.sanitizer.bypassSecurityTrustHtml(''), tabToShow: null };
    }
    
    // Current tab has no results - check if the other tab has results
    // If we're on Closed with no results, check if Active has results
    if (this.selectedTab() === ClaimTabNameEnum.Closed) {
      const activeResultsCount = this.getTabResultsCount(ClaimTabNameEnum.Active);
      if (activeResultsCount > 0) {
        const verb = this.getAreIs(activeResultsCount);
        const plural = activeResultsCount === 1 ? '' : 's';
        const msg = `There ${verb} <strong>${activeResultsCount} Active</strong> claim${plural} for '<strong>${safeSearchText}</strong>.'&nbsp;`;
        return {
          message: this.sanitizer.bypassSecurityTrustHtml(msg),
          tabToShow: ClaimTabNameEnum.Active
        };
      }
    }
    
    // If we're on Active with no results, check if Closed has results  
    if (this.selectedTab() === ClaimTabNameEnum.Active) {
      const closedResultsCount = this.getTabResultsCount(ClaimTabNameEnum.Closed);
      if (closedResultsCount > 0) {
        const verb = this.getAreIs(closedResultsCount);
        const plural = closedResultsCount === 1 ? '' : 's';
        const msg = `There ${verb} <strong>${closedResultsCount} Closed</strong> claim${plural} for '<strong>${safeSearchText}</strong>.'&nbsp;`;
        return {
          message: this.sanitizer.bypassSecurityTrustHtml(msg),
          tabToShow: ClaimTabNameEnum.Closed
        };
      }
    }
    
    // No results in either tab - no cross-tab suggestion
    return { message: this.sanitizer.bypassSecurityTrustHtml(''), tabToShow: null };
  });

  readonly shouldShowCrossTabSuggestion = computed(() => {
    const hasSearchText = !!this.searchTextSignal()?.trim();
    const hasNoResults = this.filteredClaims().length === 0;
    const messageData = this.noClaimsResultsMessage();
    const hasMessage = !!messageData.message;
    
    // Don't show cross-tab suggestions when tabs are combined
    return hasSearchText && hasNoResults && hasMessage && !this.combineTabsSignal();
  });

  readonly shouldShowEmptyState = computed(() => {
    // Calculate filtered claims count directly from signals for proper reactivity
    const claims = this.claimsSignal();
    const selectedTab = this.selectedTab();
    
    let filteredCount = 0;
    if (this.combineTabsSignal()) {
      filteredCount = claims.length;
    } else if (selectedTab === ClaimTabNameEnum.Active) {
      filteredCount = claims.filter((item) => item.status !== ClaimStatusEnum.Closed).length;
    } else if (selectedTab === ClaimTabNameEnum.Closed) {
      filteredCount = claims.filter((item) => item.status === ClaimStatusEnum.Closed).length;
    } else {
      filteredCount = claims.length;
    }
    
    return filteredCount === 0;
  });

  readonly resultsCountMessage = computed(() => {
    const searchText = this.searchTextSignal();
    const totalClaims = this.totalClaimsSignal();

    if (!searchText && totalClaims) {
      const count = totalClaims;
      const plural = count === 1 ? '' : 's';
      const message = `<strong>${count} total claim${plural}</strong>`;
      return this.sanitizer.bypassSecurityTrustHtml(message);
    }
    if (searchText) {
      // Show total results for the search term - use total count from backend API
      const count = totalClaims;
      const pluralResult = count === 1 ? '' : 's';
      const safeSearchText = this.sanitizeText(searchText);
      const message = `<strong>${count} result${pluralResult}</strong> for '<strong>${safeSearchText}</strong>'`;
      return this.sanitizer.bypassSecurityTrustHtml(message);
    }
    return this.sanitizer.bypassSecurityTrustHtml('');
  });

  readonly sortIconAscending = computed(() => {
    const currentField = this.searchParameter.sortField;
    const currentDirection = this.searchParameter.sortDirection;

    const ascending: Record<string, boolean> = {};
    [
      ClientClaimFieldEnum.ClaimId,
      ClientClaimFieldEnum.Name,
      ClientClaimFieldEnum.ClaimType,
      ClientClaimFieldEnum.Status,
      ClientClaimFieldEnum.Received,
      ClientClaimFieldEnum.Started,
      ClientClaimFieldEnum.Division,
      ClientClaimFieldEnum.LeaveType
    ].forEach((key) => {
      ascending[key] = currentDirection === SearchSortDirection.Ascending && currentField === key;
    });
    return ascending;
  });

  // Performance optimization: Pre-computed tab badges to avoid function calls in template
  readonly tabBadges = computed(() => {
    const badges: Record<ClaimTabNameEnum, BadgeData[]> = {
      [ClaimTabNameEnum.Active]: [],
      [ClaimTabNameEnum.Closed]: [],
      [ClaimTabNameEnum.All]: []
    };

    // Check if badges should be shown (search or filter applied)
    const hasSearch = !!this.searchTextSignal()?.trim();
    const hasClaimTypeFilter = this.claimTypeFiltersSignal().length > 0;
    const showBadges = hasSearch || hasClaimTypeFilter;

    if (!showBadges) return badges;

    // Calculate counts directly from reactive claimsSignal
    const claims = this.claimsSignal();
    const activeClaims = claims.filter((item) => item.status !== ClaimStatusEnum.Closed).length;
    const closedClaims = claims.filter((item) => item.status === ClaimStatusEnum.Closed).length;
    const allClaims = claims.length;

    badges[ClaimTabNameEnum.Active] = [{ value: activeClaims.toString(), variant: 'default' }];
    badges[ClaimTabNameEnum.Closed] = [{ value: closedClaims.toString(), variant: 'default' }];
    badges[ClaimTabNameEnum.All] = [{ value: allClaims.toString(), variant: 'default' }];

    return badges;
  });

  readonly tooltipBodies = computed(() => {
    const bodies: Record<string, string> = {};
    this.tableHeaders.forEach((header) => {
      if (header.tooltipContent?.body) {
        bodies[header.key] = header.tooltipContent.body;
      }
    });
    return bodies;
  });

  // Performance optimization: Pre-computed tooltip data to avoid function calls in template
  readonly tooltipHeadings = computed(() => {
    const headings: Record<string, string> = {};
    this.tableHeaders.forEach((header) => {
      if (header.tooltipContent?.heading) {
        headings[header.key] = header.tooltipContent.heading;
      }
    });
    return headings;
  });

  /*=== Lifecycle hooks ===*/

  ngOnInit() {
    // :  Table headers for the claims table
    this.tableHeaders = [
      { text: 'Claim #', key: ClientClaimFieldEnum.ClaimId, sortable: true },
      { text: 'Name', key: ClientClaimFieldEnum.Name, sortable: true },
      { text: 'Claim type', key: ClientClaimFieldEnum.ClaimType, sortable: true },
      { text: 'Status', key: ClientClaimFieldEnum.Status, sortable: true },
      { text: 'Received', key: ClientClaimFieldEnum.Received, sortable: true },
      {
        text: 'Start',
        key: ClientClaimFieldEnum.Started,
        sortable: true,
        icon: 'info_outline',
        tooltipContent: {
          heading: 'Start',
          body: ''
        }
      },
      { text: 'Leave type', key: ClientClaimFieldEnum.LeaveType, sortable: false },
      { text: 'Division', key: ClientClaimFieldEnum.Division, sortable: true }
    ];

    // Get user context information
    const userContext = this.userContextService.get();
    const userType = userContext.symClass;

    // :  Get plan codes and policy numbers for group and set up filters
    this.retrieveInitialData();

    this.showRedesignMessage = this.showRedesignMessage = environment.routeFlags?.showClaimsSearchNew === false;

    if (this.showRedesignMessage) {
      this.claimsBodyHeader = 'This feature is currently being redesigned';

      const detailFillerFunction = this.getPageDetailsFiller(userType.toLowerCase());
      const { buttonLabel, buttonUrl, cardBody } = detailFillerFunction();

      this.detailsButtonLabel = buttonLabel;
      this.detailsURL = buttonUrl;
      this.cardBody = cardBody;
    }

    // Setup debounced search
    this.setupDebouncedSearch();
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  /*=== Public methods ===*/

  // Activates the specified tab and updates the tab state
  activateTab(tabHeader: ClaimTabNameEnum) {
    if (this.selectedTab() === tabHeader) {
      return;
    }
 
    this.selectedTab.set(tabHeader);
    const tabsComponent = this.tabsComponent();
    if (tabsComponent && typeof tabsComponent.activateTab === 'function') {
      tabsComponent.activateTab(tabHeader);
    }
  }

  createNewClaim(): void {
    this.router.navigate(['/benefits/claims/create'], {
      queryParamsHandling: 'preserve'
    });
  }

  // Returns filtered claims based on tab/toggle - now reactive to signal changes
  readonly filteredClaims = computed(() => {
    const claims = this.claimsSignal();
    
    if (this.combineTabsSignal()) {
      return claims;
    }

    if (this.selectedTab() === ClaimTabNameEnum.Active) {
      // Show all except Closed
      return claims.filter((item) => item.status !== ClaimStatusEnum.Closed);
    }

    if (this.selectedTab() === ClaimTabNameEnum.Closed) {
      return claims.filter((item) => item.status === ClaimStatusEnum.Closed);
    }

    // Fallback - show all
    return claims;
  });

  getHeaderTextByKey(key: string): string {
    const header = this.tableHeaders.find((h) => h.key === key);
    return header?.text || key;
  }

  getPageDetailsFiller(userType: string): () => IDetailsPageLabels {
    let detailFillerFunction;
    switch (userType) {
      case UserRole.Employee:
        detailFillerFunction = this.getEmployeePageDetails;
        break;
      default:
        detailFillerFunction = this.getPolicyHolderPageDetails;
        break;
    }

    return detailFillerFunction;
  }

  // Whether to show badges (true if any filter/search is applied)
  get showTabBadges(): boolean {
    const hasSearch = !!this.searchParameter.searchText?.trim();
    const hasClaimTypeFilter =
      Array.isArray(this.searchParameter.filters.claimTypes) && this.searchParameter.filters.claimTypes.length > 0;
    return hasSearch || hasClaimTypeFilter;
  }

  // Returns the tooltip for the Start column based on plan codes
  get startColumnTooltip(): string {
    const codes = this.planCodes || [];
    const defaultTooltip = 'This is the start date of the leave. For life claims, this is the date of death.';
    if (codes.length === 0) {
      // Default message if no plan codes
      return defaultTooltip;
    }

    let hasLife = false;
    let hasNonLife = false;

    for (const code of codes) {
      if (LIFE_PLAN_CODES.has(code)) hasLife = true;
      if (NON_LIFE_PLAN_CODES.has(code)) hasNonLife = true;
    }

    if (hasLife && !hasNonLife) {
      // Life only
      return 'This is the date of death.';
    } else if (!hasLife && hasNonLife) {
      // Non-life only
      return 'This is the start date of the leave.';
    } else if (hasLife && hasNonLife) {
      // Both
      return defaultTooltip;
    }
    // Fallback
    return defaultTooltip;
  }

  // Returns the count of results for a specific tab
  getTabBadges(tab: ClaimTabNameEnum): BadgeData[] {
    if (!this.showTabBadges) return [];
    const count = this.tabBadgeCounts[tab];
    // Always show a badge if filters/search are applied, even if count is zero
    return [{ value: count.toString(), variant: 'default' }];
  }

  getTooltipBodyByKey(key: string): string {
    const header = this.tableHeaders.find((h) => h.key === key);
    return header?.tooltipContent?.body || '';
  }

  getTooltipHeadingByKey(key: string): string {
    const header = this.tableHeaders.find((h) => h.key === key);
    return header?.tooltipContent?.heading || '';
  }

  loadPaginatedClaimsData(
    claimsServiceClient: ClaimsServiceClient = this.claimsServiceClient,
    policyNumbers: string[] = this.policyNumbers,
    searchParameter: IClaimSearchParameters = this.searchParameter
  ) {
    // :  Get real claims data from API
    this.retrieveClaimTableItems(claimsServiceClient, policyNumbers, searchParameter);

    /*
    // :  Get simulated paginated data from local claims data service (that reads from JSON file)
    const paginatedClaimsData = this.localClaimsDataReaderService.getPaginatedClaimsData(this.searchParameter);

    // Store the total count
    this.totalClaimsSignal.set(paginatedClaimsData.totalResults);

    // Transform the claims data for the current page to table items
    const claimsTableItems = paginatedClaimsData.claims.map((claimData) => claimDataToClaimTableItem(claimData));
    */

    // Note: Signal updates are now handled in the API response callback
  }

  onClearFilters() {
    // :  Clear all filters
    this.searchParameter.filters.claimTypes = [];

    this.loadPaginatedClaimsData(this.claimsServiceClient, this.policyNumbers, this.searchParameter);
  }

  onClickTableHeader(fieldName: ClientClaimFieldEnum) {
    // If clicking the same field, toggle direction
    if (this.searchParameter.sortField === fieldName) {
      const newDirection =
        this.searchParameter.sortDirection === SearchSortDirection.Ascending
          ? SearchSortDirection.Descending
          : SearchSortDirection.Ascending;

      this.sortDirection$.next(newDirection);
      this.searchParameter.sortDirection = newDirection;
    } else {
      // New field, set to ascending by default
      this.sortField$.next(fieldName);
      this.sortDirection$.next(SearchSortDirection.Ascending);
      this.searchParameter.sortField = fieldName;
      this.searchParameter.sortDirection = SearchSortDirection.Ascending;
    }

    this.loadPaginatedClaimsData(this.claimsServiceClient, this.policyNumbers, this.searchParameter);
  }

  /**
   * Handles keyboard navigation for table headers
   * Only responds to Enter and Space keys for accessibility compliance
   */
  onKeydownTableHeader(
    event: KeyboardEvent,
    fieldName: ClientClaimFieldEnum) {
    // Only handle Enter and Space keys (WCAG 2.1 guidelines)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // Prevent default behavior (e.g., page scroll for Space)
      this.onClickTableHeader(fieldName);
    }
  }

  onPageChange(page: number) {
    this.page$.next(page);
    this.searchParameter.page = page;

    this.loadPaginatedClaimsData(this.claimsServiceClient, this.policyNumbers, this.searchParameter);
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize$.next(pageSize);
    this.searchParameter.pageSize = pageSize;
    this.searchParameter.page = 1; // Reset to first page when changing page size

    this.loadPaginatedClaimsData(this.claimsServiceClient, this.policyNumbers, this.searchParameter);
  }

  onSearch(): void {
    this.searchText$.next(this.searchText);
    this.searchParameter.searchText = this.searchText;
    this.searchParameter.page = 1; // Reset to first page when searching
  }

  onSearchInput(searchText: string) {
    // Update the displayed search text
    this.searchText = searchText;
    // Pass search input to the debouncer
    this.searchInputSubject.next(searchText);
  }

  // Handles tab change events
  onTabChange(tabHeader: string) {
    this.activateTab(tabHeader as ClaimTabNameEnum);
  }

  // Handle viewing claim detail
  viewClaimDetail(claimId: string): void {
    this.router.navigate(['/benefits/claims/', claimId], { queryParamsHandling: 'preserve' });
  }

  /*=== Private Methods ===*/

  // Returns 'is' or 'are' based on the count
  private getAreIs(count: number): string {
    return count === 1 ? 'is' : 'are';
  }

  private getEmployeePageDetails(): IDetailsPageLabels {
    return {
      buttonLabel: 'Visit LAMS',
      buttonUrl: environment.claimDetailEmployeeCTAUrl,
      cardBody: `Check back soon for our improved experience. In the meantime, please visit the Life Absence Management Systems (LAMS) if you have access.`
    };
  }

  private getPolicyHolderPageDetails(): IDetailsPageLabels {
    return {
      buttonLabel: 'Visit the Claims Management Portal',
      buttonUrl: environment.claimDetailPolicyHolderCTAUrl,
      cardBody: `Check back soon for our improved experience. In the meantime, please visit the Claims Management Portal and navigate to the desired claim.`
    };
  }

  // Returns the count of results for the specified tab, considering search text.
  private getTabResultsCount(tabName: ClaimTabNameEnum.Active | ClaimTabNameEnum.Closed): number {
    const claims = this.claimsSignal();
    
    let count = 0;
    if (tabName === ClaimTabNameEnum.Active) {
      count = claims.filter((item) => item.status !== ClaimStatusEnum.Closed).length;
    } else if (tabName === ClaimTabNameEnum.Closed) {
      count = claims.filter((item) => item.status === ClaimStatusEnum.Closed).length;
    }
    
    return count;
  }

  // Checks if the item matches the search text.
  // Returns true if search text is empty or if the item matches the search criteria.
  private matchesSearch(item: IClaimTableItem): boolean {
    return this.matchesSearchWithText(item, this.searchText);
  }

  // Helper method to check if item matches search with provided text
  private matchesSearchWithText(item: IClaimTableItem, searchText: string): boolean {
    if (!searchText) return true;
    const term = searchText.toLowerCase();
    return item.name.toLowerCase().includes(term) || item.claimId.toLowerCase().includes(term);
  }

  private retrieveClaimTableItems(
    claimsServiceClient: ClaimsServiceClient,
    policyNumbers: string[],
    searchParameter: IClaimSearchParameters
  ) {
    const paginationCriteria: SearchClaimPaginationCriteria = {
      hasNext: false,
      hasPrevious: false,
      pageNumber: searchParameter.page,
      pageSize: searchParameter.pageSize,
      pageTotal: 0
    };

    const searchCriteria: SearchClaimSearchCriteria = {
      claimNumber: undefined,
      claimStatus: undefined,
      claimTypes: searchParameter.filters.claimTypes.map((multiSelectOption) =>
        clientClaimTypeToClaimType(multiSelectOption.key as ClientClaimTypeEnum)
      ),
      dateRangeReceived: undefined,
      dateRangeStart: undefined,
      divisionName: undefined,
      employee: undefined,
      searchTerm: searchParameter.searchText
    };

    const sortCriteria: SearchClaimSortCriteria = {
      sortColumn: clientClaimFieldToSearchClaimSortColumn(searchParameter.sortField),
      sortDirection: searchSortDirectionToSearchClaimSortDirection(searchParameter.sortDirection)
    };

    const searchClaimRequest: SearchClaimRequest = {
      headerValue: undefined,
      paginationCriteria: paginationCriteria,
      policyNumbers: policyNumbers,
      searchCriteria: searchCriteria,
      sortCriteria: sortCriteria
    };

    // :  Get real claims data from API
    claimsServiceClient.searchClaim(searchClaimRequest).subscribe({
      next: (response: ApiResponseOfSearchClaimResponse | undefined) => {
        // :  Handle successful response
        const searchClaimResponse = response?.data;

        const claimsTableItems =
          searchClaimResponse?.data?.map((claimData) => searchClaimDataToClaimTableItem(claimData)) || [];

        this.totalClaimsSignal.set(searchClaimResponse?.totalCount || 0);

        // WHEN WE NO LONGER NEED MOCK DATA, WE REMOVE BELOW
        // const mergedClaimsTableItems: IClaimTableItem[] = [...claimsTableItems, ...mockApiClaimsData];
        // this.claimsTableItems = mergedClaimsTableItems;
        this.claimsTableItems = claimsTableItems;
        
        // Update reactive signals for tab badge computation (moved here after API response)
        this.claimsSignal.set(this.claimsTableItems);
        this.searchTextSignal.set(searchParameter.searchText || '');
        this.claimTypeFiltersSignal.set(searchParameter.filters.claimTypes || []);

        // Update tab badge counts after claimsTableItems is set
        this.updateTabBadgeCounts();
        
        // return mergedClaimsTableItems; //mock data return, remove later
        return claimsTableItems;
      },
      error: (error) => {
        // :  Handle error
        console.error('Error fetching search claims data:', error);
        return [];
      }
    });

    // return []; // Return an empty array if the API call fails
  }

  private retrieveInitialData() {

    // :  Policy numbers
    const policyNumbersArray = this.policyService.policyNumbers;
    const policyNumbersSet = new Set(policyNumbersArray);
    this.policyNumbers = Array.from(policyNumbersSet).sort();

    // :  Plan codes
    const planCodesArray = this.policyService.coverageCodes;
    const planCodesSet = new Set(planCodesArray);
    this.planCodes = Array.from(planCodesSet).sort();

    // Update Start column tooltip after planCodes are loaded
    const startHeader = this.tableHeaders.find((h) => h.key === ClientClaimFieldEnum.Started);
    if (startHeader && startHeader.tooltipContent) {
      startHeader.tooltipContent.body = this.startColumnTooltip;
    }

    const claimTypesSorted: ClientClaimTypeEnum[] = planCodesToClaimTypeMapping(planCodesSet);

    const multiSelectArray = claimTypesSorted.map((claimType) => {
      const claimTypeLabel = claimType.toString();

      const multiSelectOption: MultiSelectOption = {
        key: claimTypeLabel,
        label: claimTypeLabel,
        selected: false
      };

      return multiSelectOption;
    });

    // Load claims with pagination
    this.loadPaginatedClaimsData(this.claimsServiceClient, this.policyNumbers, this.searchParameter);

    // :  Filter options
    this.filters = [
      {
        name: this.getHeaderTextByKey(ClientClaimFieldEnum.ClaimType),
        model: new BehaviorSubject<MultiSelectOption[]>(multiSelectArray),
        type: FILTER_TYPE.DROPDOWN,
        onSelectionsUpdated: (filterName: string, newSelections: MultiSelectOption[]) => {
          this.searchParameter.filters.claimTypes = newSelections.filter((option) => option.selected);

          this.loadPaginatedClaimsData(this.claimsServiceClient, this.policyNumbers, this.searchParameter);
        }
      }
    ];

    this.isLoading = false;
  }

  // Sanitizes text to prevent XSS attacks by escaping HTML special characters.
  private sanitizeText(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private setupDebouncedSearch() {
    const debounceTimeLength = 250;

    this.searchSubscription = this.searchInputSubject
      .pipe(
        debounceTime(debounceTimeLength),
        distinctUntilChanged(),
        switchMap((searchText: string) => {
          // Emit the search text to the BehaviorSubject
          this.searchText$.next(searchText);
          return this.searchText$;
        })
      )
      .subscribe((searchText) => {
        this.searchParameter.searchText = searchText;
        this.searchParameter.page = 1;

        this.loadPaginatedClaimsData(this.claimsServiceClient, this.policyNumbers, this.searchParameter);
      });
  }

  // Updates tab badge counts based on current filtered results
  private updateTabBadgeCounts(claimsTableItems: IClaimTableItem[] = this.claimsTableItems) {
    // Only update if filters/search are applied
    if (!this.showTabBadges) {
      this.tabBadgeCounts = {
        [ClaimTabNameEnum.Active]: 0,
        [ClaimTabNameEnum.Closed]: 0,
        [ClaimTabNameEnum.All]: 0
      };
      return;
    }

    this.tabBadgeCounts[ClaimTabNameEnum.Active] = claimsTableItems.filter(
      (item) => item.status !== ClaimStatusEnum.Closed
    ).length;

    this.tabBadgeCounts[ClaimTabNameEnum.Closed] = claimsTableItems.filter(
      (item) => item.status === ClaimStatusEnum.Closed
    ).length;

    this.tabBadgeCounts[ClaimTabNameEnum.All] = claimsTableItems.length;
  }
}
